import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ShoppingCart, Heart, Eye, Package, Loader2, Star, Truck, Shield, Zap, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { printifyService, printifyUtils, type PrintifyProduct } from '@/services/printify';
import { useAuth } from '@/hooks/useAuth';

export function PrintifyStore() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('featured');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<PrintifyProduct | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<any>(null);
  const [quantity, setQuantity] = useState(1);
  const [wishlist, setWishlist] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [shopId, setShopId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Fetch shops first to get the shop ID
  const { data: shops, isLoading: shopsLoading, error: shopsError } = useQuery({
    queryKey: ['printify-shops'],
    queryFn: async () => {
      try {
        console.log('Fetching Printify shops...');
        const result = await printifyService.getShops();
        console.log('Shops fetched successfully:', result);
        return result;
      } catch (err) {
        console.error('Error fetching shops:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch shops');
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once
  });

  // Set shop ID when shops are loaded
  useEffect(() => {
    if (shops && shops.length > 0) {
      console.log('Setting shop ID:', shops[0].id);
      setShopId(shops[0].id);
    }
  }, [shops]);

  // Fetch products from the selected shop
  const { data: productsData, isLoading: productsLoading, error: productsError } = useQuery({
    queryKey: ['printify-products', shopId, currentPage],
    queryFn: async () => {
      if (!shopId) {
        throw new Error('No shop ID available');
      }
      try {
        console.log('Fetching products for shop:', shopId, 'page:', currentPage);
        const result = await printifyService.getShopProducts(shopId, currentPage, 24);
        console.log('Products fetched successfully:', result);
        return result;
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch products');
        throw err;
      }
    },
    enabled: !!shopId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once
  });

  const products = productsData?.data || [];

  // Handle errors
  useEffect(() => {
    if (shopsError) {
      setError(shopsError instanceof Error ? shopsError.message : 'Failed to fetch shops');
    }
    if (productsError) {
      setError(productsError instanceof Error ? productsError.message : 'Failed to fetch products');
    }
  }, [shopsError, productsError]);

  // Filter and sort products
  const filteredProducts = products
    .filter(product => {
      const matchesCategory = selectedCategory === 'all' || 
        printifyUtils.determineCategory(product.tags) === selectedCategory;
      const matchesSearch = product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           product.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch && product.is_enabled;
    })
    .sort((a, b) => {
      const defaultVariantA = a.variants.find(v => v.is_default) || a.variants[0];
      const defaultVariantB = b.variants.find(v => v.is_default) || b.variants[0];
      
      switch (sortBy) {
        case 'price-low':
          return (defaultVariantA?.price || 0) - (defaultVariantB?.price || 0);
        case 'price-high':
          return (defaultVariantB?.price || 0) - (defaultVariantA?.price || 0);
        case 'featured':
          return (a.tags.includes('featured') ? 1 : 0) - (b.tags.includes('featured') ? 1 : 0);
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

  // Get unique categories from products
  const categories = Array.from(new Set(products.map(p => printifyUtils.determineCategory(p.tags))));

  const toggleWishlist = (productId: string) => {
    setWishlist(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
    toast({
      title: wishlist.includes(productId) ? "Removed from wishlist" : "Added to wishlist",
      description: "Your wishlist has been updated",
    });
  };

  const handlePurchase = async (product: PrintifyProduct) => {
    if (!selectedVariant) {
      toast({
        title: "Please select a variant",
        description: "Choose size, color, and other options before purchasing",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create order through our backend
      const response = await fetch('/api/printify/create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          shopId,
          productId: product.id,
          variantId: selectedVariant.id,
          quantity,
          printProviderId: product.print_provider_id,
          printAreas: selectedVariant.print_areas || [],
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create order');
      }

      const orderData = await response.json();
      
      toast({
        title: "Order Created!",
        description: `Your order #${orderData.order_number} has been created successfully.`,
      });

      // Close dialog and reset
      setSelectedProduct(null);
      setSelectedVariant(null);
      setQuantity(1);
    } catch (error) {
      toast({
        title: "Order Failed",
        description: error instanceof Error ? error.message : "Failed to create order",
        variant: "destructive",
      });
    }
  };

  const handleVariantSelect = (variant: any) => {
    setSelectedVariant(variant);
  };

  // Show error state
  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Connection Error</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <Button 
          onClick={() => {
            setError(null);
            window.location.reload();
          }}
          className="bg-gradient-to-r from-purple-600 to-purple-700"
        >
          Retry Connection
        </Button>
        <div className="mt-4 text-sm text-gray-500">
          <p>This might be due to:</p>
          <ul className="list-disc list-inside mt-2">
            <li>Network connectivity issues</li>
            <li>Printify API service being down</li>
            <li>Invalid API credentials</li>
          </ul>
        </div>
      </div>
    );
  }

  if (shopsLoading || productsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!shopId) {
    return (
      <div className="text-center py-12">
        <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No Printify shop found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Search Printify products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
        </div>
        <div className="flex gap-2">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="featured">Featured</SelectItem>
              <SelectItem value="price-low">Price: Low to High</SelectItem>
              <SelectItem value="price-high">Price: High to Low</SelectItem>
              <SelectItem value="newest">Newest First</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Category Tabs */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
        <TabsList className="grid grid-cols-2 md:grid-cols-5 w-full">
          <TabsTrigger value="all">All Products</TabsTrigger>
          {categories.map(category => (
            <TabsTrigger key={category} value={category} className="capitalize">
              {category}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={selectedCategory} className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => {
              const defaultVariant = product.variants.find(v => v.is_default) || product.variants[0];
              const mainImage = printifyUtils.getBestImage(product.images);
              
              return (
                <Card 
                  key={product.id} 
                  className="group hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
                  onClick={() => setSelectedProduct(product)}
                >
                  <div className="aspect-square relative overflow-hidden bg-gradient-to-br from-purple-50 to-blue-50">
                    <img
                      src={mainImage}
                      alt={product.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {product.tags.includes('featured') && (
                      <Badge className="absolute top-2 left-2 bg-purple-600">
                        <Star className="w-3 h-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="icon"
                        variant="secondary"
                        className="rounded-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleWishlist(product.id);
                        }}
                      >
                        <Heart 
                          className={`h-4 w-4 ${wishlist.includes(product.id) ? 'fill-red-500 text-red-500' : ''}`} 
                        />
                      </Button>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-lg mb-1 line-clamp-2">{product.title}</h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-purple-600">
                        {printifyUtils.formatPrice(defaultVariant?.price || 0)}
                      </span>
                      <Button 
                        size="sm" 
                        className="bg-gradient-to-r from-purple-600 to-purple-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProduct(product);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No products found matching your criteria</p>
            </div>
          )}

          {/* Pagination */}
          {productsData && productsData.last_page > 1 && (
            <div className="flex justify-center mt-8">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 py-2 text-sm">
                  Page {currentPage} of {productsData.last_page}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(productsData.last_page, prev + 1))}
                  disabled={currentPage === productsData.last_page}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Product Detail Dialog */}
      <Dialog open={!!selectedProduct} onOpenChange={() => {
        setSelectedProduct(null);
        setSelectedVariant(null);
        setQuantity(1);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedProduct && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">{selectedProduct.title}</DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Product Images */}
                <div className="space-y-4">
                  <div className="aspect-square relative overflow-hidden rounded-lg bg-gradient-to-br from-purple-50 to-blue-50">
                    <img
                      src={printifyUtils.getBestImage(selectedProduct.images)}
                      alt={selectedProduct.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Additional Images */}
                  {selectedProduct.images.length > 1 && (
                    <div className="grid grid-cols-4 gap-2">
                      {selectedProduct.images.slice(0, 4).map((image, index) => (
                        <div key={index} className="aspect-square rounded-md overflow-hidden">
                          <img
                            src={image.src}
                            alt={`${selectedProduct.title} ${index + 1}`}
                            className="w-full h-full object-cover cursor-pointer hover:opacity-80"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Product Details */}
                <div className="space-y-6">
                  <div>
                    <p className="text-gray-600 mb-4">{selectedProduct.description}</p>
                    
                    {/* Features */}
                    {selectedProduct.tags.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-semibold mb-2">Features:</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedProduct.tags.map((tag, index) => (
                            <Badge key={index} variant="secondary" className="capitalize">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Variant Selection */}
                  <div className="space-y-4">
                    <h4 className="font-semibold">Select Options:</h4>
                    
                    {/* Variant Options */}
                    {selectedProduct.variants.length > 0 && (
                      <div className="grid grid-cols-2 gap-2">
                        {selectedProduct.variants.map((variant) => (
                          <Button
                            key={variant.id}
                            variant={selectedVariant?.id === variant.id ? "default" : "outline"}
                            className="justify-start"
                            onClick={() => handleVariantSelect(variant)}
                          >
                            <div className="text-left">
                              <div className="font-medium">{variant.title}</div>
                              <div className="text-sm text-gray-600">
                                {printifyUtils.formatPrice(variant.price)}
                              </div>
                            </div>
                          </Button>
                        ))}
                      </div>
                    )}

                    {/* Quantity */}
                    <div className="flex items-center gap-4">
                      <label className="font-medium">Quantity:</label>
                      <Select value={quantity.toString()} onValueChange={(value) => setQuantity(parseInt(value))}>
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                            <SelectItem key={num} value={num.toString()}>{num}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Pricing and Purchase */}
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <span className="text-3xl font-bold text-purple-600">
                          {selectedVariant ? printifyUtils.formatPrice(selectedVariant.price) : 'Select variant'}
                        </span>
                        {selectedVariant && quantity > 1 && (
                          <div className="text-sm text-gray-600">
                            Total: {printifyUtils.formatPrice(selectedVariant.price * quantity)}
                          </div>
                        )}
                      </div>
                      <Badge variant={selectedProduct.is_enabled ? "default" : "secondary"}>
                        {selectedProduct.is_enabled ? "In Stock" : "Out of Stock"}
                      </Badge>
                    </div>

                    {/* Benefits */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="text-center text-sm">
                        <Truck className="h-6 w-6 text-green-600 mx-auto mb-2" />
                        <div>Free Shipping</div>
                      </div>
                      <div className="text-center text-sm">
                        <Shield className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                        <div>Quality Guarantee</div>
                      </div>
                      <div className="text-center text-sm">
                        <Zap className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                        <div>Fast Production</div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700"
                        disabled={!selectedProduct.is_enabled || !selectedVariant}
                        onClick={() => handlePurchase(selectedProduct)}
                      >
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Add to Cart
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => toggleWishlist(selectedProduct.id)}
                      >
                        <Heart 
                          className={`h-4 w-4 ${wishlist.includes(selectedProduct.id) ? 'fill-red-500 text-red-500' : ''}`} 
                        />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
